export interface Option<T extends string = string> {
    value: T
    label: string
}

export type JobCategory =
    | 'software'
    | 'data-ai'
    | 'infrastructure'
    | 'security'
    | 'design'
    | 'product'
    | 'quality'
    | 'it'
    | 'solutions'
    | 'leadership'

export type ExperienceLevel = 'intern' | 'entry' | 'mid' | 'senior' | 'staff' | 'lead'

export type WorkType = 'remote' | 'hybrid' | 'onsite'

export type EmploymentType = 'full_time' | 'part_time' | 'contract' | 'internship' | 'temporary'

export const JOB_CATEGORIES: Array<Option<JobCategory>> = [
    { value: 'software', label: 'Software engineering' },
    { value: 'data-ai', label: 'Data and AI' },
    { value: 'infrastructure', label: 'Infrastructure and DevOps' },
    { value: 'security', label: 'Security' },
    { value: 'design', label: 'Design' },
    { value: 'product', label: 'Product management' },
    { value: 'quality', label: 'Quality and testing' },
    { value: 'it', label: 'IT and support' },
    { value: 'solutions', label: 'Solutions engineering' },
    { value: 'leadership', label: 'Engineering leadership' }
]

export const EXPERIENCE_LEVELS: Array<Option<ExperienceLevel>> = [
    { value: 'intern', label: 'Internship' },
    { value: 'entry', label: 'Entry level' },
    { value: 'mid', label: 'Mid level' },
    { value: 'senior', label: 'Senior' },
    { value: 'staff', label: 'Staff and principal' },
    { value: 'lead', label: 'Manager and director' }
]

export const WORK_TYPES: Array<Option<WorkType>> = [
    { value: 'remote', label: 'Remote' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'onsite', label: 'On-site' }
]

export const EMPLOYMENT_TYPES: Array<Option<EmploymentType>> = [
    { value: 'full_time', label: 'Full-time' },
    { value: 'part_time', label: 'Part-time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' },
    { value: 'temporary', label: 'Temporary' }
]

export const POSTED_WITHIN: Array<Option> = [
    { value: '1', label: 'Last 24 hours' },
    { value: '3', label: 'Last 3 days' },
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' }
]

export const SALARY_FLOORS: Array<Option> = [
    { value: '75000', label: '$75k+' },
    { value: '100000', label: '$100k+' },
    { value: '150000', label: '$150k+' },
    { value: '200000', label: '$200k+' },
    { value: '250000', label: '$250k+' }
]

export const JOB_SORTS: Array<Option> = [
    { value: 'recent', label: 'Most recent' },
    { value: 'relevant', label: 'Most relevant' },
    { value: 'salary', label: 'Highest salary' },
    { value: 'match', label: 'Best match' }
]

export const APPLICATION_STAGES: Array<Option> = [
    { value: 'wishlist', label: 'To apply' },
    { value: 'applied', label: 'Applied' },
    { value: 'interviewing', label: 'Interviewing' },
    { value: 'offer', label: 'Offer' },
    { value: 'closed', label: 'Closed' }
]

export const CLOSED_REASONS: Array<Option> = [
    { value: 'rejected', label: 'Not selected' },
    { value: 'withdrawn', label: 'Withdrew' },
    { value: 'accepted', label: 'Accepted an offer' },
    { value: 'expired', label: 'Role closed' }
]

export const REPORT_REASONS: Array<Option> = [
    { value: 'closed', label: 'The job is no longer open' },
    { value: 'wrong', label: 'Details are wrong' },
    { value: 'not-tech', label: 'Not a technology role' },
    { value: 'spam', label: 'Spam or suspicious' },
    { value: 'other', label: 'Something else' }
]

const labelMap = (options: Array<Option>) => options.reduce<Record<string, string>>((all, option) => {
    all[option.value] = option.label
    return all
}, {})

const CATEGORY_LABELS = labelMap(JOB_CATEGORIES)
const LEVEL_LABELS = labelMap(EXPERIENCE_LEVELS)
const WORK_TYPE_LABELS = labelMap(WORK_TYPES)
const EMPLOYMENT_LABELS = labelMap(EMPLOYMENT_TYPES)
const STAGE_LABELS = labelMap(APPLICATION_STAGES)

export const categoryLabel = (value?: string | null) => (value && CATEGORY_LABELS[value]) || 'Technology'
export const levelLabel = (value?: string | null) => (value && LEVEL_LABELS[value]) || null
export const workTypeLabel = (value?: string | null) => (value && WORK_TYPE_LABELS[value]) || null
export const employmentLabel = (value?: string | null) => (value && EMPLOYMENT_LABELS[value]) || null
export const stageLabel = (value?: string | null) => (value && STAGE_LABELS[value]) || 'To apply'

export const isOneOf = <T extends string>(options: Array<Option<T>>, value: unknown): value is T =>
    typeof value === 'string' && options.some(option => option.value === value)
